import { useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// MUI
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMaterialUIController } from "context";

export default function CalendarView({ events, onDateClick, onEventClick }) {
  const calendarRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setCurrentMonth(newMonth);
    if (calendarRef.current && newMonth) {
      calendarRef.current.getApi().gotoDate(newMonth);
    }
  };

  const handleDatesSet = (arg) => {
    const date = arg.view.calendar.getDate();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    setCurrentMonth(`${year}-${month}`);
  };

  // Filtrar eventos cancelados para não aparecerem no calendário
  const activeEvents = Array.isArray(events) ? events.filter(e => e.status !== 'cancelled') : [];

  return (
    <MDBox
      mt={3}
      sx={{
        "& .fc-toolbar-title": {
          fontSize: isMobile ? "1.1rem !important" : "1.5rem",
          color: darkMode ? "#fff" : "inherit",
        },
        "& .fc-button": {
            backgroundColor: darkMode ? "#344767" : "inherit",
            color: darkMode ? "#fff" : "inherit",
            borderColor: darkMode ? "#344767" : "inherit",
        },
        "& .fc-icon": {
            color: darkMode ? "#fff" : "inherit",
        },
        "& .fc-col-header-cell": {
            backgroundColor: darkMode ? "#202940" : "inherit",
            backgroundColor: darkMode ? "#202940 !important" : "transparent",
        },
        "& .fc-theme-standard th": {
            backgroundColor: darkMode ? "#202940 !important" : "transparent",
        },
        "& .fc-col-header-cell-cushion": {
            color: darkMode ? "#fff" : "inherit",
        },
        "& .fc-daygrid-day-number": {
            color: darkMode ? "#fff" : "inherit",
        },
        "& .fc-scrollgrid, & .fc-theme-standard td, & .fc-theme-standard th": {
            borderColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "inherit",
        },
        "& .fc-popover": {
            backgroundColor: darkMode ? "#202940" : "#fff",
            color: darkMode ? "#fff" : "inherit",
        },
        "& .fc-popover-header": {
            backgroundColor: darkMode ? "#202940" : "#f0f2f5",
            color: darkMode ? "#fff" : "inherit",
        },
      }}
    >
      <MDBox display="flex" justifyContent={isMobile ? "center" : "flex-end"} mb={2}>
        <MDInput
          type="month"
          label="Ir para mês"
          value={currentMonth}
          onChange={handleMonthChange}
          InputLabelProps={{ shrink: true }}
          sx={{ 
            width: isMobile ? "100%" : 180,
            "& .MuiInputBase-input": { color: darkMode ? "#fff" : "inherit" },
            "& .MuiInputLabel-root": { color: darkMode ? "#fff" : "inherit" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : "inherit" },
          }}
        />
      </MDBox>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale="pt-br"
        height="auto" // Ajusta altura automaticamente
        aspectRatio={isMobile ? 0.75 : 1.35} // Células mais altas no mobile
        dayMaxEvents={true} // Evita que o calendário estoure verticalmente
        events={activeEvents}
        dateClick={onDateClick}
        eventClick={onEventClick}
        datesSet={handleDatesSet}
        buttonText={{
          today: "Hoje",
          month: isMobile ? "Mês" : "Mês",
          week: "Semana",
          day: "Dia",
        }}
        headerToolbar={{
          left: isMobile ? "prev,next" : "prev,next today",
          center: "title",
          right: isMobile ? "dayGridMonth,timeGridDay" : "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        views={{
          dayGridMonth: {
            titleFormat: { year: 'numeric', month: isMobile ? 'short' : 'long' }
          }
        }}
      />
    </MDBox>
  );
}
